<template>
  <div>
    <!-- Header -->
    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-0 font-weight-bold text-dark">
            <i class="fa fa-user-plus mr-2 text-primary"></i>
            Crear Nuevo Socio
          </h4>
          <p class="text-muted mb-0">Complete la información del nuevo socio</p>
        </div>
        <div class="text-right">
          <small class="text-muted d-block">Campos obligatorios</small>
          <small class="text-danger font-weight-bold">
            <i class="fa fa-asterisk"></i> Marcados en rojo
          </small>
        </div>
      </div>
    </div>

    <card class="shadow-sm border-0">
      <div class="d-flex flex-column flex-md-row">
        <!-- Columna principal con formulario -->
        <div class="col-12 col-md-9">
          <!-- Sección móvil de avatar y controles -->
          <div class="d-flex d-md-none flex-column justify-content-center mb-4">
            <!-- Avatar Card -->
            <div class="card shadow-sm border-0 mb-3">
              <div class="card-body text-center py-4">
                <div
                  class="rounded-circle mx-auto overflow-hidden d-flex shadow-sm avatar-container"
                  :class="!avatarSrc ? 'bg-gradient-primary' : ''"
                >
                  <img
                    v-if="avatarSrc"
                    :src="avatarSrc"
                    alt="Imagen del socio"
                    class="w-100 h-100 object-fit-cover"
                  />
                  <i
                    v-else
                    class="fa fa-user text-white mx-auto my-auto avatar-icon"
                  ></i>
                </div>
                
                <button
                  class="btn btn-primary btn-sm rounded-pill mt-3"
                  @click="$emit('open-avatar-modal')"
                >
                  <i class="fa fa-camera mr-2"></i>
                  Subir foto
                </button>
              </div>
            </div>
          
            <!-- Controles básicos móvil -->
            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-building mr-1"></i>
                    Sucursal de origen <span class="text-danger">*</span>
                  </label>
                  <el-select
                    v-model="newSocio.selectedOriginBranch"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :class="{
                      'select-danger': !newSocio.selectedOriginBranch,
                    }"
                  >
                    <el-option
                      v-for="(item, index) in branches"
                      :key="index"
                      :label="item.nombreSucursal"
                      :value="JSON.stringify(item)"
                    ></el-option>
                  </el-select>
                  <small
                    v-if="!newSocio.selectedOriginBranch"
                    class="text-danger"
                  >Campo obligatorio</small>
                </div>
              </div>
              
              <div class="col-6">
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-lock mr-1"></i>
                    Acceso a Sucursal <span class="text-danger">*</span>
                  </label>
                  <el-select
                    v-model="newSocio.accesoSucursal.estadoAcceso"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :class="{
                      'select-danger': !newSocio.accesoSucursal.estadoAcceso,
                    }"
                  >
                    <el-option
                      v-for="(item, index) in estadosAcceso"
                      :key="index"
                      :label="item.glosa"
                      :value="item.glosa"
                    >
                      <span class="d-flex align-items-center">
                        <i 
                          class="fa mr-2"
                          :class="item.glosa === 'AUTORIZADO' ? 'fa-check text-success' : 'fa-times text-danger'"
                        ></i>
                        {{ item.glosa }}
                      </span>
                    </el-option>
                  </el-select>
                  <small
                    v-if="!newSocio.accesoSucursal.estadoAcceso"
                    class="text-danger"
                  >Campo obligatorio</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Información de Socio -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-user mr-2 text-primary"></i>
                Información Personal
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-12 col-md-5">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-id-card mr-1"></i>
                      Nombre completo <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Ingrese nombre completo"
                      v-model.trim="newSocio.nombreSocio"
                      :class="{ 'is-invalid': !newSocio.nombreSocio }"
                    />
                    <small v-if="!newSocio.nombreSocio" class="text-danger">
                      Campo obligatorio
                    </small>
                  </div>
                </div>
          
                <el-tooltip
                  :open-delay="200"
                  placement="top"
                >
                  <template #content>
                    <span v-if="newSocio.esExtranjero">
                      Ingresa el ID o Pasaporte<br />
                      Ej: "ABC123"
                    </span>
                    <span v-else>
                      Ingresa el RUT sin puntos<br />
                      Ej: "11111111-1"
                    </span>
                  </template>
                  <div class="col-6 col-md-3">
                    <div class="form-group">
                      <label class="form-label small text-secondary mb-1">
                        <i class="fa fa-fingerprint mr-1"></i>
                        {{ newSocio.esExtranjero ? 'ID / Pasaporte' : 'RUT' }} <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control text-uppercase"
                        :placeholder="newSocio.esExtranjero ? 'Ej: ABC123' : '11111111-1'"
                        v-model="newSocio.rut"
                        @input="handleRutInput"
                        :class="{
                          'is-invalid': !newSocio.rut || !rutValidator,
                        }"
                      />
                      <small v-if="!newSocio.rut" class="text-danger">
                        Campo obligatorio
                      </small>
                      <small
                        v-if="!rutValidator && newSocio.rut && !newSocio.esExtranjero"
                        class="text-danger"
                      >
                        RUT inválido
                      </small>
                    </div>
                  </div>
                </el-tooltip>
                
                <div class="col-6 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-venus-mars mr-1"></i>
                      Sexo
                    </label>
                    <el-select
                      v-model="newSocio.sexo"
                      placeholder="Seleccionar..."
                      class="w-100"
                    >
                      <el-option label="HOMBRE" value="HOMBRE">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-mars text-primary mr-2"></i>
                          Hombre
                        </span>
                      </el-option>
                      <el-option label="MUJER" value="MUJER">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-venus text-danger mr-2"></i>
                          Mujer
                        </span>
                      </el-option>
                      <el-option label="NO ESPECIFICADO" value="">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-genderless text-muted mr-2"></i>
                          No especificado
                        </span>
                      </el-option>
                    </el-select>
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group text-center">
                    <label class="form-label small text-secondary mb-1 d-block">
                      <i class="fa fa-globe mr-1"></i>
                      ¿Extranjero?
                    </label>
                    <p-switch
                      type="primary"
                      class="mx-auto"
                      on-text="SI"
                      off-text="NO"
                      v-model="newSocio.esExtranjero"
                    ></p-switch>
                  </div>
                </div>
          
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-calendar mr-1"></i>
                      Fecha de nacimiento
                    </label>
                    <el-date-picker
                      type="date"
                      class="w-100"
                      placeholder="Seleccionar fecha"
                      format="dd-MM-yyyy"
                      value-format="dd-MM-yyyy"
                      v-model="newSocio.informacionAdicional.fechaNacimiento"
                    >
                    </el-date-picker>
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-user-tag mr-1"></i>
                      Perfil <span class="text-danger">*</span>
                    </label>
                    <el-select
                      placeholder="Seleccionar perfil"
                      v-model="newSocio.perfilCuenta"
                      class="w-100"
                      :class="{ 'select-danger': !newSocio.perfilCuenta }"
                    >
                      <el-option
                        v-for="(item, index) in perfiles"
                        :key="index"
                        :label="item.glosa"
                        :value="item.glosa"
                      >
                      </el-option>
                    </el-select>
                    <small v-if="!newSocio.perfilCuenta" class="text-danger">
                      Campo obligatorio
                    </small>
                  </div>
                </div>

                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-info-circle mr-1"></i>
                      Estado <span class="text-danger">*</span>
                    </label>
                    <el-select
                      placeholder="Seleccionar estado"
                      v-model="newSocio.estadoCuenta"
                      class="w-100"
                      :class="{ 'select-danger': !newSocio.estadoCuenta }"
                    >
                      <el-option
                        v-for="(item, index) in estados"
                        :key="index"
                        :label="item.glosa"
                        :value="item.glosa"
                      >
                        <span class="d-flex align-items-center">
                          <span 
                            class="badge mr-2"
                            :class="getEstadoBadgeClass(item.glosa)"
                          >
                            &nbsp;
                          </span>
                          {{ item.glosa }}
                        </span>
                      </el-option>
                    </el-select>
                    <small v-if="!newSocio.estadoCuenta" class="text-danger">
                      Campo obligatorio
                    </small>
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group text-center">
                    <label class="form-label small text-secondary mb-1 d-block">
                      <i class="fa fa-bell mr-1"></i>
                      Notificar vencimientos
                    </label>
                    <el-tooltip
                      :open-delay="200"
                      placement="top"
                    >
                      <template #content>
                        Notifica el vencimiento de<br />las membresías por correo
                      </template>
                      <p-switch
                        type="success"
                        class="mx-auto mb-0"
                        on-text="SI"
                        off-text="NO"
                        v-model="newSocio.informacionAdicional.notificarVencimiento"
                      ></p-switch>
                    </el-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Información de Contacto -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-envelope mr-2 text-primary"></i>
                Información de Contacto
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-at mr-1"></i>
                      Email principal <span class="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      class="form-control"
                      placeholder="correo@ejemplo.com"
                      v-model.trim="newSocio.autenticacion.email"
                      :class="{
                        'is-invalid': !newSocio.autenticacion.email || !emailValidator,
                      }"
                    />
                    <small
                      v-if="!newSocio.autenticacion.email"
                      class="text-danger"
                    >
                      Campo obligatorio
                    </small>
                    <small
                      v-if="!emailValidator && newSocio.autenticacion.email"
                      class="text-danger"
                    >
                      Email inválido
                    </small>
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-envelope-o mr-1"></i>
                      Email secundario
                    </label>
                    <input
                      type="email"
                      class="form-control"
                      placeholder="correo2@ejemplo.com"
                      v-model.trim="newSocio.informacionContacto.emailSecundario"
                    />
                  </div>
                </div>

                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-phone mr-1"></i>
                      Teléfono
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="+56 2 1234 5678"
                      v-model.trim="newSocio.informacionContacto.telefonoFijo"
                    />
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-mobile mr-1"></i>
                      Celular
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="+56 9 1234 5678"
                      v-model.trim="newSocio.informacionContacto.celular"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Información Geográfica -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-map-marker-alt mr-2 text-primary"></i>
                Información Geográfica
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-flag mr-1"></i>
                      País
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Chile"
                      v-model.trim="newSocio.informacionGeografica.pais"
                    />
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-city mr-1"></i>
                      Ciudad
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Santiago"
                      v-model.trim="newSocio.informacionGeografica.ciudad"
                    />
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-map mr-1"></i>
                      Región
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Metropolitana"
                      v-model.trim="newSocio.informacionGeografica.region"
                    />
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-home mr-1"></i>
                      Comuna
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Las Condes"
                      v-model.trim="newSocio.informacionGeografica.comuna"
                    />
                  </div>
                </div>
                
                <div class="col-8 col-md-4">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-road mr-1"></i>
                      Dirección
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Av. Principal 123"
                      v-model.trim="newSocio.informacionGeografica.direccion"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Redes Sociales -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-share-alt mr-2 text-primary"></i>
                Redes Sociales
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-facebook-square mr-1" style="color: #4267b2"></i>
                      Facebook
                    </label>
                    <input
                      type="text"
                      placeholder="usuario.facebook"
                      class="form-control"
                      v-model.trim="newSocio.informacionContacto.facebook"
                    />
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-twitter-square mr-1" style="color: #1da1f2"></i>
                      Twitter
                    </label>
                    <input
                      type="text"
                      placeholder="@usuario"
                      class="form-control"
                      v-model.trim="newSocio.informacionContacto.twitter"
                    />
                  </div>
                </div>

                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-instagram mr-1" style="color: #f56040"></i>
                      Instagram
                    </label>
                    <input
                      type="text"
                      placeholder="@usuario"
                      class="form-control"
                      v-model.trim="newSocio.informacionContacto.instagram"
                    />
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-linkedin mr-1" style="color: #0e76a8"></i>
                      LinkedIn
                    </label>
                    <input
                      type="text"
                      placeholder="usuario-linkedin"
                      class="form-control"
                      v-model.trim="newSocio.informacionContacto.linkedin"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Botones de acción -->
          <div class="d-flex justify-content-center mt-5 mb-3">
            <el-tooltip
              :open-delay="200"
              placement="top"
              :disabled="isValidForm && rutValidator"
            >
              <template #content>
                <div class="text-center">
                  <i class="fa fa-exclamation-triangle mb-2 fa-2x"></i>
                  <p class="mb-0">Completa todos los campos obligatorios</p>
                </div>
              </template>
              <button
                class="btn btn-primary btn-lg px-5 mr-3"
                :disabled="!isValidForm || !rutValidator"
                @click="handleCreate"
              >
                <i class="fa fa-save mr-2"></i>
                Crear Socio
              </button>
            </el-tooltip>
            
            <button
              class="btn btn-outline-secondary btn-lg px-5"
              @click="handleCancel"
            >
              <i class="fa fa-times mr-2"></i>
              Cancelar
            </button>
          </div>
        </div>

        <!-- Columna lateral con avatar y controles (desktop) -->
        <div class="col-12 col-md-3 d-none d-md-block">
          <div class="sticky-top" style="top: 20px;">
            <!-- Avatar Card -->
            <div class="card shadow-sm border-0 mb-4">
              <div class="card-body text-center py-4">
                <div
                  class="rounded-circle mx-auto overflow-hidden d-flex shadow-sm avatar-container-lg"
                  :class="!avatarSrc ? 'bg-gradient-primary' : ''"
                >
                  <img
                    v-if="avatarSrc"
                    :src="avatarSrc"
                    alt="Imagen del socio"
                    class="w-100 h-100 object-fit-cover"
                  />
                  <i
                    v-else
                    class="fa fa-user text-white mx-auto my-auto avatar-icon-lg"
                  ></i>
                </div>
                
                <button
                  class="btn btn-primary btn-sm rounded-pill mt-3"
                  @click="$emit('open-avatar-modal')"
                >
                  <i class="fa fa-camera mr-2"></i>
                  Subir foto
                </button>
              </div>
            </div>

            <!-- Controles Adicionales -->
            <div class="card shadow-sm border-0">
              <div class="card-header bg-light border-0">
                <h6 class="mb-0">
                  <i class="fa fa-cog mr-2 text-primary"></i>
                  Configuración
                </h6>
              </div>
              <div class="card-body">
        
                <div v-if="esPerfilCarga" class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-user-tie mr-1"></i>
                    RUT Titular Convenio
                  </label>
                  <input
                    type="text"
                    placeholder="11111111-1"
                    class="form-control form-control-sm"
                    v-model.trim="newSocio.informacionAdicional.rutTitularConvenio"
                  />
                  <small
                    v-if="
                      newSocio.informacionAdicional.rutTitularConvenio &&
                      !validateRut(newSocio.informacionAdicional.rutTitularConvenio)
                    "
                    class="text-danger"
                  >RUT inválido</small>
                </div>
                
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-building mr-1"></i>
                    Sucursal de origen <span class="text-danger">*</span>
                  </label>
                  <el-select
                    v-model="newSocio.selectedOriginBranch"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :class="{
                      'select-danger': !newSocio.selectedOriginBranch,
                    }"
                  >
                    <el-option
                      v-for="(item, index) in branches"
                      :key="index"
                      :label="item.nombreSucursal"
                      :value="JSON.stringify(item)"
                    ></el-option>
                  </el-select>
                  <small
                    v-if="!newSocio.selectedOriginBranch"
                    class="text-danger"
                  >Campo obligatorio</small>
                </div>
                
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-lock mr-1"></i>
                    Acceso a Sucursal <span class="text-danger">*</span>
                  </label>
                  <el-select
                    v-model="newSocio.accesoSucursal.estadoAcceso"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :class="{
                      'select-danger': !newSocio.accesoSucursal.estadoAcceso,
                    }"
                  >
                    <el-option
                      v-for="(item, index) in estadosAcceso"
                      :key="index"
                      :label="item.glosa"
                      :value="item.glosa"
                    >
                      <span class="d-flex align-items-center">
                        <i 
                          class="fa mr-2"
                          :class="item.glosa === 'AUTORIZADO' ? 'fa-check text-success' : 'fa-times text-danger'"
                        ></i>
                        {{ item.glosa }}
                      </span>
                    </el-option>
                  </el-select>
                  <small
                    v-if="!newSocio.accesoSucursal.estadoAcceso"
                    class="text-danger"
                  >Campo obligatorio</small>
                </div>

                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-file-contract mr-1"></i>
                    Número de Contrato
                  </label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Ej: CTR-2024-001"
                    v-model.trim="newSocio.informacionAdicional.contrato"
                  />
                </div>

                <div class="form-group mb-0">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-comment-alt mr-1"></i>
                    Comentarios / Notas
                  </label>
                  <textarea
                    class="form-control form-control-sm"
                    placeholder="Información adicional del socio..."
                    v-model="newSocio.informacionAdicional.detalleFicha"
                    :rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { Tooltip, Select, Option, DatePicker } from 'element-ui';
import PSwitch from 'src/components/UIComponents/Switch.vue';

export default {
  name: 'SocioCrear',
  components: {
    [Tooltip.name]: Tooltip,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    PSwitch
  },
  props: {
    branches: {
      type: Array,
      default: () => []
    },
    estados: {
      type: Array,
      default: () => []
    },
    estadosAcceso: {
      type: Array,
      default: () => []
    },
    perfiles: {
      type: Array,
      default: () => []
    },
    avatarData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      newSocio: {
        selectedOriginBranch: '',
        nombreSocio: '',
        esExtranjero: false,
        rut: '',
        sexo: '',
        perfilCuenta: '',
        estadoCuenta: '',
        autenticacion: {
          email: '',
          password: '123456'
        },
        informacionGeografica: {
          pais: '',
          ciudad: '',
          region: '',
          comuna: '',
          direccion: ''
        },
        informacionContacto: {
          emailSecundario: '',
          telefonoFijo: '',
          celular: '',
          facebook: '',
          twitter: '',
          instagram: '',
          linkedin: ''
        },
        informacionAdicional: {
          notificarVencimiento: true,
          fechaNacimiento: '',
          contrato: '',
          detalleFicha: '',
          rutTitularConvenio: ''
        },
        accesoSucursal: {
          estadoAcceso: ''
        },
        avatar: {
          imagenMiniatura: '',
          imagenPerfil: ''
        }
      }
    };
  },
  computed: {
    esPerfilCarga() {
      return this.newSocio.perfilCuenta === 'CARGA';
    },
    
    avatarSrc() {
      if (this.newSocio.avatar?.imagenPerfil) {
        return `data:image/jpg;base64,${this.newSocio.avatar.imagenPerfil}`;
      }
      return null;
    },
    
    isValidForm() {
      return (
        this.newSocio.nombreSocio &&
        this.newSocio.rut &&
        this.newSocio.perfilCuenta &&
        this.newSocio.estadoCuenta &&
        this.newSocio.autenticacion.email &&
        this.newSocio.accesoSucursal.estadoAcceso &&
        this.newSocio.selectedOriginBranch
      );
    },
    
    rutValidator() {
      if (this.newSocio.esExtranjero) {
        return true;
      }
      return this.validateRut(this.newSocio.rut);
    },
    
    emailValidator() {
      const regex = /^[a-zA-Z0-9._&-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/;
      return regex.test(this.newSocio.autenticacion.email);
    }
  },
  watch: {
    avatarData: {
      handler(newVal) {
        if (newVal) {
          this.newSocio.avatar = {
            imagenMiniatura: newVal.imagenMiniatura,
            imagenPerfil: newVal.imagenPerfil
          };
        }
      },
      deep: true
    },
    'newSocio.esExtranjero'(newVal) {
      // Limpiar el campo RUT cuando cambia el estado de extranjero
      this.newSocio.rut = '';
    }
  },
  methods: {
    handleRutInput(event) {
      const input = event.target.value;
      
      if (this.newSocio.esExtranjero) {
        // Para extranjeros: texto libre en mayúsculas
        this.newSocio.rut = input.toUpperCase();
      } else {
        // Para chilenos: números, letras (K) y solo 1 guión
        // Convertir a mayúsculas
        let formatted = input.toUpperCase();
        
        // Remover caracteres no permitidos (solo números, K y guión)
        formatted = formatted.replace(/[^0-9K\-]/g, '');
        
        // Contar guiones
        const guiones = (formatted.match(/\-/g) || []).length;
        
        // Si hay más de un guión, remover los extras (mantener solo el primero)
        if (guiones > 1) {
          let firstGuionFound = false;
          formatted = formatted.split('').filter(char => {
            if (char === '-') {
              if (!firstGuionFound) {
                firstGuionFound = true;
                return true;
              }
              return false;
            }
            return true;
          }).join('');
        }
        
        this.newSocio.rut = formatted;
      }
    },
    
    validateRut(rut) {
      if (!rut) return false;
      
      const cleanRut = rut.trim();
      const regex = /^\d{7,8}-[\dkK]$/;
      
      if (!regex.test(cleanRut)) {
        return false;
      }
      
      const parts = cleanRut.split('-');
      const cuerpo = parts[0];
      const dv = parts[1].toUpperCase();
      
      let suma = 0;
      let multiplo = 2;
      
      for (let i = cuerpo.length - 1; i >= 0; i--) {
        suma += multiplo * cuerpo.charAt(i);
        multiplo = multiplo === 7 ? 2 : multiplo + 1;
      }
      
      let dvEsperado = 11 - (suma % 11);
      if (dvEsperado === 11) {
        dvEsperado = '0';
      } else if (dvEsperado === 10) {
        dvEsperado = 'K';
      } else {
        dvEsperado = dvEsperado.toString();
      }
      
      return dv === dvEsperado;
    },
    
    getEstadoBadgeClass(estado) {
      switch (estado) {
        case 'ACTIVA':
          return 'badge-success';
        case 'PENDIENTE':
          return 'badge-warning';
        case 'INACTIVA':
          return 'badge-secondary';
        case 'BLOQUEADA':
          return 'badge-danger';
        case 'ELIMINADA':
          return 'badge-dark';
        default:
          return 'badge-secondary';
      }
    },
    
    handleCreate() {
      this.$emit('create', this.newSocio);
    },
    
    handleCancel() {
      this.$emit('cancel');
    },
    
    resetForm() {
      this.newSocio = {
        selectedOriginBranch: '',
        nombreSocio: '',
        esExtranjero: false,
        rut: '',
        sexo: '',
        perfilCuenta: '',
        estadoCuenta: '',
        autenticacion: {
          email: '',
          password: '123456'
        },
        informacionGeografica: {
          pais: '',
          ciudad: '',
          region: '',
          comuna: '',
          direccion: ''
        },
        informacionContacto: {
          emailSecundario: '',
          telefonoFijo: '',
          celular: '',
          facebook: '',
          twitter: '',
          instagram: '',
          linkedin: ''
        },
        informacionAdicional: {
          notificarVencimiento: true,
          fechaNacimiento: '',
          contrato: '',
          detalleFicha: '',
          rutTitularConvenio: ''
        },
        accesoSucursal: {
          estadoAcceso: ''
        },
        avatar: {
          imagenMiniatura: '',
          imagenPerfil: ''
        }
      };
    }
  }
};
</script>

<style scoped>
/* Avatares */
.avatar-container {
  width: 120px;
  height: 120px;
}

.avatar-container-lg {
  width: 150px;
  height: 150px;
}

.avatar-icon {
  font-size: 60px;
}

.avatar-icon-lg {
  font-size: 80px;
}

/* Gradiente para avatares */
.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Object fit */
.object-fit-cover {
  object-fit: cover;
}

/* Select con error */
.select-danger .el-input__inner {
  border-color: #dc3545 !important;
}

/* Cards mejoradas */
.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
}

/* Form labels */
.form-label {
  font-weight: 500;
  color: #495057;
}

/* Form groups mejorados */
.form-group {
  margin-bottom: 1rem;
}

/* Input groups mejorados */
.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

/* Badge mejorados */
.badge {
  padding: 0.25em 0.5em;
  font-weight: 500;
}

/* Botones mejorados */
.btn-lg {
  font-weight: 500;
  letter-spacing: 0.5px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #68428e 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Sticky sidebar */
.sticky-top {
  position: sticky;
}

/* Select personalizado */
.el-select {
  width: 100%;
}

.el-select >>> .el-input__inner {
  border-radius: 0.25rem;
  border-color: #ced4da;
  font-size: 0.875rem;
}

.el-select >>> .el-input__inner:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

/* Card header mejorado */
.card-header {
  background-color: #f8f9fa !important;
}

/* Shadow suave */
.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important;
}

/* Transiciones */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Responsividad */
@media (max-width: 768px) {
  .avatar-container {
    width: 100px;
    height: 100px;
  }
  
  .avatar-icon {
    font-size: 50px;
  }
  
  .btn-lg {
    padding: 0.375rem 1rem;
    font-size: 0.875rem;
  }
}
</style>
